import React from 'react';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, Typography } from '@mui/material';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

const PaginationControls = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    itemsPerPageOptions = [3, 6, 9, 12, 18, 24],
    showItemsPerPage = true,
    maxPageButtons = 5
}) => {
    const handlePrevPage = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleFirstPage = () => {
        onPageChange(1);
    };

    const handleLastPage = () => {
        onPageChange(totalPages);
    };

    // Calculate which page buttons to show
    const getPageNumbers = () => {
        const pages = [];
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        // Adjust start if we're near the end
        if (endPage - startPage < maxPageButtons - 1) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: { xs: 3, md: 4 },
                mb: { xs: 1, md: 2 },
                gap: { xs: 2, md: 2 },
                padding: { xs: '12px', sm: '16px', md: '20px' },
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: { xs: '8px', md: '12px' },
                border: '1px solid rgba(216, 176, 53, 0.2)'
            }}
        >
            {/* Items per page selector */}
            {showItemsPerPage && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    width: { xs: '100%', md: 'auto' }
                }}>
                    <FormControl
                        size="small"
                        sx={{
                            minWidth: { xs: '100%', sm: 120 },
                            '& .MuiOutlinedInput-root': {
                                color: '#333',
                                backgroundColor: 'white',
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                '& fieldset': {
                                    borderColor: 'rgba(216, 176, 53, 0.5)',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#d8b035',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#d8b035',
                                }
                            },
                            '& .MuiInputLabel-root': {
                                color: '#666',
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                '&.Mui-focused': {
                                    color: '#d8b035'
                                }
                            },
                            '& .MuiSelect-icon': {
                                color: '#d8b035'
                            }
                        }}
                    >
                        <InputLabel id="items-per-page-label">Per Page</InputLabel>
                        <Select
                            labelId="items-per-page-label"
                            value={itemsPerPage}
                            label="Per Page"
                            onChange={(e) => onItemsPerPageChange(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: 'white',
                                        color: '#333',
                                        '& .MuiMenuItem-root': {
                                            fontSize: { xs: '0.875rem', md: '1rem' },
                                            '&:hover': {
                                                backgroundColor: 'rgba(216, 176, 53, 0.2)'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(216, 176, 53, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(216, 176, 53, 0.4)'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {itemsPerPageOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option} items
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            display: { xs: 'none', sm: 'block' },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Showing {startIndex}-{endIndex} of {totalItems}
                    </Typography>
                </Box>
            )}

            {/* Page navigation */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                flexWrap: 'wrap',
                justifyContent: 'center',
                width: { xs: '100%', md: 'auto' }
            }}>
                {/* First page button - Hidden on mobile */}
                <Button
                    onClick={handleFirstPage}
                    disabled={currentPage === 1}
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        minWidth: { sm: '36px', md: '40px' },
                        padding: { sm: '6px', md: '8px' },
                        border: '1px solid rgba(216, 176, 53, 0.5)',
                        backgroundColor: 'transparent',
                        color: currentPage === 1 ? 'rgba(255, 255, 255, 0.3)' : '#d8b035',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: currentPage === 1 ? 'transparent' : 'rgba(216, 176, 53, 0.1)',
                            borderColor: '#d8b035'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <FaAngleDoubleLeft />
                </Button>

                {/* Previous button */}
                <Button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    sx={{
                        minWidth: { xs: '60px', sm: '70px', md: '80px' },
                        padding: { xs: '6px 10px', sm: '7px 14px', md: '8px 16px' },
                        border: '1px solid rgba(216, 176, 53, 0.5)',
                        backgroundColor: 'transparent',
                        color: currentPage === 1 ? 'rgba(255, 255, 255, 0.3)' : '#d8b035',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                        '&:hover': {
                            backgroundColor: currentPage === 1 ? 'transparent' : 'rgba(216, 176, 53, 0.1)',
                            borderColor: '#d8b035'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <FaChevronLeft style={{ marginRight: '4px', fontSize: '0.75rem' }} />
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Prev</Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>P</Box>
                </Button>

                {/* Page numbers - Responsive display */}
                {pageNumbers[0] > 1 && (
                    <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        px: { xs: 0.5, sm: 1 },
                        fontSize: { xs: '0.75rem', md: '1rem' },
                        display: { xs: 'none', sm: 'block' }
                    }}>...</Typography>
                )}

                {pageNumbers.map(pageNum => (
                    <Button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        sx={{
                            minWidth: { xs: '32px', sm: '36px', md: '40px' },
                            padding: { xs: '6px 8px', sm: '7px 10px', md: '8px 12px' },
                            border: '1px solid rgba(216, 176, 53, 0.5)',
                            backgroundColor: currentPage === pageNum ? '#d8b035' : 'transparent',
                            color: currentPage === pageNum ? '#fff' : '#d8b035',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                            fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                            '&:hover': {
                                backgroundColor: currentPage === pageNum ? '#c5a02f' : 'rgba(216, 176, 53, 0.1)',
                                borderColor: '#d8b035'
                            }
                        }}
                    >
                        {pageNum}
                    </Button>
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                    <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        px: { xs: 0.5, sm: 1 },
                        fontSize: { xs: '0.75rem', md: '1rem' },
                        display: { xs: 'none', sm: 'block' }
                    }}>...</Typography>
                )}

                {/* Next button */}
                <Button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    sx={{
                        minWidth: { xs: '60px', sm: '70px', md: '80px' },
                        padding: { xs: '6px 10px', sm: '7px 14px', md: '8px 16px' },
                        border: '1px solid rgba(216, 176, 53, 0.5)',
                        backgroundColor: 'transparent',
                        color: currentPage === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#d8b035',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                        '&:hover': {
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'rgba(216, 176, 53, 0.1)',
                            borderColor: '#d8b035'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Next</Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>N</Box>
                    <FaChevronRight style={{ marginLeft: '4px', fontSize: '0.75rem' }} />
                </Button>

                {/* Last page button - Hidden on mobile */}
                <Button
                    onClick={handleLastPage}
                    disabled={currentPage === totalPages}
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        minWidth: { sm: '36px', md: '40px' },
                        padding: { sm: '6px', md: '8px' },
                        border: '1px solid rgba(216, 176, 53, 0.5)',
                        backgroundColor: 'transparent',
                        color: currentPage === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#d8b035',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'rgba(216, 176, 53, 0.1)',
                            borderColor: '#d8b035'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <FaAngleDoubleRight />
                </Button>
            </Box>

            {/* Mobile info - Shows on mobile only */}
            <Typography
                variant="body2"
                sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'block', sm: 'none' },
                    width: '100%',
                    textAlign: 'center',
                    mt: 1
                }}
            >
                Page {currentPage} of {totalPages} • {startIndex}-{endIndex} of {totalItems} items
            </Typography>
        </Box>
    );
};

export default PaginationControls;
